import React, {useCallback, useEffect, useMemo, useRef} from "react";
import {observer} from "mobx-react-lite";
import {useTranslation} from "react-i18next";
import EventTemplateCategory from "../../../stores/domainStore/pages/models/eventTemplateCategory";
import EventTemplateCategoriesList from "../../organisms/eventTemplateCategoriesList/eventTemplateCategoriesList";
import HeaderProgressBar from "../../atoms/headerProgressBar/headerProgressBar";
import "./eventCatalogueTemplate.sass";
import EventTemplateSummaryItem from "../../../stores/domainStore/pages/models/eventTemplateSummaryItem";
import ButtonIcon from "../../atoms/button/buttonIcon";
import {IconSource} from "../../../shared/enums/iconSource";
import Switcher from "../../atoms/switcher/switcher";
import {ObservableList} from "../../../stores/observables/observableList";
import {KeyValue} from "../../../stores/observables/keyValue";
import {EventTemplateStatus} from "../../../shared/enums/eventTemplateStatus";
import SearchBox, {SearchBoxType} from "../../atoms/input/searchBox";
import {TextObservable} from "../../../stores/observables/textObservable";
import {useStores} from "../../../stores/useStores";
import {runInAction} from "mobx";
import {EventCatalogueSortField} from "../../../shared/enums/eventCatalogueSortField";
import CatalogueSearchResult from "../../organisms/catalogueSearchResult/catalogueSearchResult";
import HeaderColor from "../../atoms/headerColor/headerColor";
import {useHistory, useLocation} from "react-router";
import {QueryParams} from "../../../shared/enums/queryParams";
import {Navigation} from "../../../utils/navigation";
import {
  eventCalendarTypeDisplay,
  eventCalendarTypeFromCatalogueParam
} from "../../../shared/eventCalendarTypeHelper";
import HaloIcon from "../../molecules/haloIcon/haloIcon";

export interface IEventCatalogueTemplateProps {
  templateViewStatus: EventTemplateStatus;
  eventTemplateCategories: EventTemplateCategory[];
  isLoading: boolean;
  onAddNewTemplateClick: () => void;
  onEditTemplate: (eventTemplate: EventTemplateSummaryItem) => void;
  onSelectTemplate: (eventTemplate: EventTemplateSummaryItem) => void;
  onUpdateTemplateStatusClick: (
    eventTemplate: EventTemplateSummaryItem,
    status: EventTemplateStatus
  ) => void;
  onTemplateStatusChanged: (templateStatus: EventTemplateStatus) => void;
}

const EventCatalogueTemplate: React.FC<IEventCatalogueTemplateProps> = ({
  templateViewStatus,
  eventTemplateCategories,
  isLoading,
  onAddNewTemplateClick,
  onEditTemplate,
  onSelectTemplate,
  onUpdateTemplateStatusClick,
  onTemplateStatusChanged
}) => {
  const {
    uiStore: {eventCatalogueUiStore}
  } = useStores();

  useEffect(() => {
    runInAction(() => {
      eventCatalogueUiStore.eventCatalogueSearchText = "";
    });
  }, [eventCatalogueUiStore]);
  const {t} = useTranslation(["translation", "eventTemplate"]);
  const statuses = [
    new KeyValue(
      EventTemplateStatus.Active,
      t("eventTemplate:labels.published")
    ),
    new KeyValue(
      EventTemplateStatus.Archived,
      t("eventTemplate:labels.archived")
    )
  ];
  const templateViewOptions = new ObservableList<KeyValue>(
    statuses,
    templateViewStatus
  );

  const searchText: TextObservable = useMemo(
    () =>
      new TextObservable(
        eventCatalogueUiStore.eventCatalogueSearchText,
        undefined,
        undefined
      ),
    [eventCatalogueUiStore.eventCatalogueSearchText]
  );

  const switchTemplateStatus = () => {
    let status = EventTemplateStatus.Active;
    if (
      templateViewOptions.selectedItem?.value === EventTemplateStatus.Archived
    ) {
      status = EventTemplateStatus.Archived;
    }
    onTemplateStatusChanged(status);
    searchText.setText(eventCatalogueUiStore.eventCatalogueSearchText);
  };

  const history = useHistory();
  const catalogueType = eventCalendarTypeFromCatalogueParam(
    Navigation.getQueryParamByKey(history, QueryParams.type)
  );
  const catalogueDisplay = eventCalendarTypeDisplay[catalogueType];

  const location = useLocation();
  const search = location.search;
  const queryParams = React.useMemo(
    () => new URLSearchParams(search),
    [search]
  );
  const selectedCatalogueName = decodeURI(
    queryParams.get(QueryParams.type) || ""
  );

  useEffect(() => {
    runInAction(() => {
      eventCatalogueUiStore.selectedCatalogueName = selectedCatalogueName;
    });
  }, [eventCatalogueUiStore, selectedCatalogueName]);

  const searchAbortRef = useRef<AbortController>();
  const searchTemplates = useCallback(() => {
    if (eventCatalogueUiStore.isSearch) {
      searchAbortRef.current?.abort();
      const controller = new AbortController();
      searchAbortRef.current = controller;
      eventCatalogueUiStore.searchTemplates(
        templateViewStatus,
        EventCatalogueSortField.title,
        eventCatalogueUiStore.eventCatalogueSearchText,
        catalogueType,
        controller
      );
    }
  }, [eventCatalogueUiStore, catalogueType, templateViewStatus]);

  useEffect(() => {
    searchTemplates();
  }, [eventCatalogueUiStore.reloadCatalogueSearchResultEvent, searchTemplates]);

  useEffect(() => () => searchAbortRef.current?.abort(), []);

  const haloColor = catalogueDisplay.haloColor;
  const haloIcon = catalogueDisplay.icon;

  const newCatalogueHeader = (
    <>
      <HaloIcon iconSource={haloIcon} haloColor={haloColor} />
      {t(catalogueDisplay.catalogueTitleKey)}
    </>
  );

  return (
    <div className="card event-catalogue" data-testid="eventCatalogueTemplate">
      {haloColor && <HeaderColor color={haloColor} />}
      <div className="card-header">
        <div>
          <div>
            <div className="page-description-header">{newCatalogueHeader}</div>
            <Switcher
              options={templateViewOptions}
              onClick={switchTemplateStatus}
            />
          </div>
          <div className="search-and-button">
            <div>
              <SearchBox
                hasPrefixIcon={false}
                value={searchText}
                type={SearchBoxType.NonLive}
                showSearchIcon={true}
                placeholder={t("eventTemplate:placeholders.search")}
                onClick={() => {
                  if (searchText.text.trim() !== "") {
                    runInAction(() => {
                      eventCatalogueUiStore.isSearch = true;
                      eventCatalogueUiStore.eventCatalogueSearchText =
                        searchText.text.trim();
                      searchTemplates();
                    });
                  }
                }}
                onClear={() => {
                  searchAbortRef.current?.abort();
                  eventCatalogueUiStore.resetSearch();
                }}
              ></SearchBox>
            </div>
            <ButtonIcon
              type="primary"
              icon={IconSource.add}
              onClick={onAddNewTemplateClick}
            >
              {t("eventTemplate:labels.add")}
            </ButtonIcon>
          </div>
        </div>
      </div>
      <HeaderProgressBar progressBarLoading={isLoading} />
      {isLoading && !eventCatalogueUiStore.isSearch ? (
        <div className="no-categories" />
      ) : eventCatalogueUiStore.isSearch ? (
        <CatalogueSearchResult
          isLoading={isLoading}
          templateViewStatus={templateViewStatus}
          onEditTemplate={onEditTemplate}
          onSelectTemplate={onSelectTemplate}
          onUpdateTemplateStatusClick={(eventTemplate, status) => {
            onUpdateTemplateStatusClick(eventTemplate, status);
            searchTemplates();
          }}
        />
      ) : eventTemplateCategories.length ? (
        <EventTemplateCategoriesList
          eventTemplateCategories={eventTemplateCategories}
          templateViewStatus={templateViewStatus}
          onEditTemplate={onEditTemplate}
          onSelectTemplate={onSelectTemplate}
          onUpdateTemplateStatusClick={onUpdateTemplateStatusClick}
        />
      ) : (
        <div className="card">{t("eventTemplate:messages.noCategories")}</div>
      )}
    </div>
  );
};

export default observer(EventCatalogueTemplate);
